/**
 * Everflow Tracking Utility
 * Handles initialization and tracking for Everflow analytics
 */

import { config } from '../config/env'
import { logger } from './logger'
import { sessionStore } from './sessionKey'
import { localStore } from './localStorageKey'
import { hasInFlowBrowserCaseMatch } from './funnelUtils'

const EF_SCRIPT_DEFAULT = 'https://www.hu0po4er.com/scripts/main.js'
const EF_SCRIPT_ALTERNATE = 'https://www.cg6ttm8trk.com/scripts/main.js'
const EF_TRACKING_BASE_ALTERNATE = 'https://www.cg6ttm8trk.com'
const EF_TRACKING_BASE_DEFAULT = 'https://www.hu0po4er.com'

/** For `oid=23` (CV-1/CV-2) and `oid=54` (NAD/Sermorelin): only this adv_event_id may be sent. */
const ALTERNATE_OID_ALLOWED_ADV_EVENT_ID = 4

/**
 * Get URL parameter value
 * @param {string} name - Parameter name
 * @returns {string|null} Parameter value or null
 */
const getUrlParameter = (name) => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(name)
}

/** Everflow attribution coupon for South Africa organic traffic (#723). */
export const ORGANIC_ZA_COUPON = 'ORGANICZA'
/** Default organic affiliate_id used by EF.configure / EF.click. */
const ORGANIC_AFFILIATE_ID = '9'
/** Response BPO partner — receives all events for ZA organic sessions (#723). */
const RESPONSE_BPO_AFFILIATE_ID = '33'
/** Only offer the ORGANICZA coupon is mapped to in Everflow. */
const ORGANIC_ZA_COUPON_OFFER_ID = '1'
const EVERFLOW_COUPON_COOKIE = 'everflow_coupon_code'
const VISITOR_COUNTRY_COOKIE = 'visitor_country'
/** Affiliate an offer's click was sent with (set by the funnel and the WP site). */
const CLICK_AFFID_COOKIE_PREFIX = 'everflow_click_affid_offer_'

/**
 * Read a document cookie by name.
 * @param {string} name
 * @returns {string|null}
 */
const readDocumentCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return null
  const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`))
  if (!row) return null
  try {
    return decodeURIComponent(row.slice(name.length + 1)) || null
  } catch (_e) {
    return row.slice(name.length + 1) || null
  }
}

/**
 * Resolve visitor country: ?ef_country= (test override) → visitor_country cookie
 * (set by Cloudflare middleware from CF-IPCountry).
 * @returns {string} Uppercase ISO country code or ''
 */
export const getVisitorCountry = () => {
  const fromQuery = getUrlParameter('ef_country')
  if (fromQuery) return String(fromQuery).trim().toUpperCase()
  const fromCookie = readDocumentCookie(VISITOR_COUNTRY_COOKIE)
  return fromCookie ? String(fromCookie).trim().toUpperCase() : ''
}

/**
 * Organic (9) and Response BPO (33) never clear the ORGANICZA tag: ZA organic
 * clicks are fired as 33, so the SDK may later report affid=33 for the session.
 * @param {string|null} affid
 * @returns {boolean}
 */
const isOtherPartnerAffid = (affid) => {
  if (!affid) return false
  const value = String(affid)
  return value !== ORGANIC_AFFILIATE_ID && value !== RESPONSE_BPO_AFFILIATE_ID
}

/**
 * True when the landing/session has partner affiliate tracking other than
 * Organic (9) or Response BPO (33).
 * @returns {boolean}
 */
export const isPartnerAffiliateTraffic = () => {
  if (isOtherPartnerAffid(getUrlParameter('affid'))) {
    return true
  }

  if (getUrlParameter('aff_id2') || getUrlParameter('affid2')) {
    return true
  }

  if (typeof window !== 'undefined' && window.EF?.urlParameter) {
    if (isOtherPartnerAffid(window.EF.urlParameter('affid'))) {
      return true
    }
    if (window.EF.urlParameter('aff_id2') || window.EF.urlParameter('affid2')) {
      return true
    }
  }

  // Persisted Department Media session (same key as getOrPersistAffId2)
  if (sessionStore.getItem('everflow_dm_aff_id2')) {
    return true
  }

  // Funnel-persisted affid from an earlier page (URL often drops tracking params)
  if (isOtherPartnerAffid(sessionStore.getItem('affid'))) {
    return true
  }

  return false
}

/**
 * Persist or clear the ORGANICZA Everflow coupon cookie (shared on .sunlight.com).
 * @param {string|null} couponCode
 */
const setEverflowCouponCookie = (couponCode) => {
  if (typeof document === 'undefined') return
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const domainPart = isLocalhost ? '' : '; domain=.sunlight.com'
  const securePart = window.location.protocol === 'https:' ? '; Secure' : ''

  if (!couponCode) {
    document.cookie = `${EVERFLOW_COUPON_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}; SameSite=Lax${securePart}`
    sessionStore.removeItem(EVERFLOW_COUPON_COOKIE)
    return
  }

  document.cookie = `${EVERFLOW_COUPON_COOKIE}=${encodeURIComponent(couponCode)}; path=/${domainPart}; SameSite=Lax${securePart}`
  sessionStore.setItem(EVERFLOW_COUPON_COOKIE, couponCode)
  logger.debug(`🎟️ Everflow coupon tagged for session: ${couponCode}`)
}

/**
 * Current Everflow attribution coupon for this session (ORGANICZA when ZA organic).
 * @returns {string|null}
 */
export const getOrganicZaCouponCode = () => {
  const fromSession = sessionStore.getItem(EVERFLOW_COUPON_COOKIE)
  if (fromSession === ORGANIC_ZA_COUPON) return ORGANIC_ZA_COUPON
  const fromCookie = readDocumentCookie(EVERFLOW_COUPON_COOKIE)
  if (fromCookie === ORGANIC_ZA_COUPON) {
    sessionStore.setItem(EVERFLOW_COUPON_COOKIE, ORGANIC_ZA_COUPON)
    return ORGANIC_ZA_COUPON
  }
  return null
}

/**
 * Tag South Africa organic sessions with ORGANICZA; clear the tag for partner traffic.
 * Call on Everflow init (and safe to call again on navigation).
 * @returns {string|null} Coupon code when tagged, otherwise null
 */
export const maybeTagOrganicZaCoupon = () => {
  if (isPartnerAffiliateTraffic()) {
    if (getOrganicZaCouponCode()) {
      logger.debug('🎟️ Clearing ORGANICZA — partner affiliate traffic detected')
      setEverflowCouponCookie(null)
    }
    return null
  }

  const existing = getOrganicZaCouponCode()
  if (existing) return existing

  const country = getVisitorCountry()
  if (country === 'ZA') {
    setEverflowCouponCookie(ORGANIC_ZA_COUPON)
    return ORGANIC_ZA_COUPON
  }

  return null
}

/**
 * Whether this conversion payload should include ORGANICZA: First Purchase on
 * offer 1 only. The coupon is mapped to offer 1 in Everflow, so sending it on
 * other offers (NAD+ 3, Sermorelin 4) remaps the conversion to offer 1.
 * Those offers get Response BPO credit from the affid=33 click instead.
 * @param {Object} conversionData
 * @returns {boolean}
 */
const shouldAttachOrganicZaCoupon = (conversionData = {}) => {
  if (!getOrganicZaCouponCode()) return false
  // Default CV-1 routes send no offer_id; Everflow attributes those to the landing offer (1).
  const offerId = conversionData.offer_id ?? getEverflowLandingConfig().organicOfferId
  if (String(offerId) !== ORGANIC_ZA_COUPON_OFFER_ID) return false
  return conversionData.adv_event_id === 4 || conversionData.adv_event_id === '4'
}

/**
 * Affiliate ID for EF.click(). ZA organic sessions (ORGANICZA tagged) click as
 * Response BPO (33) so every event in the session is attributed to 33.
 *
 * @param {number|string} [fallback=9]
 * @returns {number|string}
 */
const resolveClickAffiliateId = (fallback = ORGANIC_AFFILIATE_ID) => {
  if (getOrganicZaCouponCode()) {
    return RESPONSE_BPO_AFFILIATE_ID
  }
  if (typeof window !== 'undefined' && window.EF && typeof window.EF.urlParameter === 'function') {
    return window.EF.urlParameter('affid') || fallback
  }
  return fallback
}

const getClickAffiliateCookieName = (offerId) => `${CLICK_AFFID_COOKIE_PREFIX}${offerId}`

/**
 * True when a ZA-tagged session's existing click for this offer was not sent as
 * Response BPO, e.g. the WP landing clicked as Organic before geo was known.
 * Events inherit the click's affiliate, so the offer must be re-clicked as 33.
 * @param {number|string} offerId
 * @returns {boolean}
 */
const needsResponseBpoReclick = (offerId) =>
  !!getOrganicZaCouponCode() &&
  readDocumentCookie(getClickAffiliateCookieName(offerId)) !== RESPONSE_BPO_AFFILIATE_ID

/** Offer IDs (as strings) with a Response BPO re-click in flight; conversions wait for these. */
const pendingResponseBpoReclicks = new Set()

/**
 * True when the current page is the CV-1 or CV-2 funnel (not NAD / Sermorelin).
 */
const isCv1OrCv2Funnel = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  const pathname = window.location.pathname || ''
  return (
    pathname.startsWith('/cv-1') ||
    pathname.startsWith('/cv-2') ||
    pathname.startsWith('/cv-3') ||
    pathname.startsWith('/cv-4') ||
    pathname.startsWith('/payment/return/cv')
  )
}

/**
 * True when the current page is the NAD or Sermorelin funnel.
 */
const isNadOrSermorelinFunnel = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  const pathname = window.location.pathname || ''
  return (
    pathname.startsWith('/nad') ||
    pathname.startsWith('/payment/return/nad') ||
    pathname.startsWith('/sermorelin') ||
    pathname.startsWith('/payment/return/sermorelin')
  )
}

/**
 * True when the user landed with `oid=23` on CV-1 or CV-2 (URL or EF-persisted param).
 * In this mode we only fire the purchase Everflow event (adv_event_id: 4).
 */
const isOid23EverflowSession = () => {
  if (!isCv1OrCv2Funnel()) return false
  if (getUrlParameter('oid') === '23') return true
  if (typeof window !== 'undefined' && window.EF?.urlParameter?.('oid') === '23') return true
  return false
}

/**
 * True when the user landed with `oid=54` on NAD or Sermorelin (URL or EF-persisted param).
 * Loads the alternate EF tracker (cg6ttm8trk.com) with offer_id 54.
 * In this mode we only fire the purchase Everflow event (adv_event_id: 4).
 */
const isOid54EverflowSession = () => {
  if (!isNadOrSermorelinFunnel()) return false
  if (getUrlParameter('oid') === '54') return true
  if (typeof window !== 'undefined' && window.EF?.urlParameter?.('oid') === '54') return true
  return false
}

/** Whether the current session should use the alternate Everflow tracker domain/script. */
const usesAlternateEverflowTracker = () =>
  isOid23EverflowSession() || isOid54EverflowSession()

/** Whether alternate-oid sessions should restrict EF to purchase-only (no adv_event_id in payload). */
const isAlternateOidRestrictedSession = () =>
  isOid23EverflowSession() || isOid54EverflowSession()

/** `oid` query value for logging when in a restricted alternate-oid session. */
export const getAlternateOidValue = () => {
  if (isOid23EverflowSession()) return '23'
  if (isOid54EverflowSession()) return '54'
  return null
}

/**
 * Everflow script + offer IDs for the current landing (from `oid` query param or funnel path).
 * - When `oid=23` on CV-1/CV-2, load the alternate EF tracker; conversions must include offer_id 23.
 * - When `oid=54` on NAD/Sermorelin, load the alternate EF tracker; conversions must include offer_id 54.
 * - NAD funnel routes (/nad, /nad/*, /payment/return/nad) use offer_id 3.
 * - All other routes default to offer_id 1.
 */
const getEverflowLandingConfig = () => {
  if (isNadFunnel()) {
    if (isOid54EverflowSession()) {
      return {
        scriptSrc: EF_SCRIPT_ALTERNATE,
        organicOfferId: 54,
        conversionOfferId: 54
      }
    }
    return {
      scriptSrc: EF_SCRIPT_DEFAULT,
      organicOfferId: 3,
      conversionOfferId: 3
    }
  }

  // TODO: Replace placeholder offer_id 4 with the real Sermorelin Everflow offer ID
  if (isSermorelinFunnel()) {
    if (isOid54EverflowSession()) {
      return {
        scriptSrc: EF_SCRIPT_ALTERNATE,
        organicOfferId: 54,
        conversionOfferId: 54
      }
    }
    return {
      scriptSrc: EF_SCRIPT_DEFAULT,
      organicOfferId: 4,
      conversionOfferId: 4
    }
  }

  // oid=23 alternate tracker — CV-1 and CV-2 only
  if (isOid23EverflowSession()) {
    return {
      scriptSrc: EF_SCRIPT_ALTERNATE,
      organicOfferId: 23,
      conversionOfferId: 23
    }
  }

  // Default for all other funnels
  return {
    scriptSrc: EF_SCRIPT_DEFAULT,
    organicOfferId: 1,
    conversionOfferId: null
  }
}

/**
 * Get funnel-specific Everflow tracking marker cookie name based on the
 * current funnel's organic offer_id.
 *
 * This allows different funnels (e.g. /cv-1 and /nad) to track independently
 * in the same browser. When a user lands on /cv-1 we set
 * `everflow_tracked_offer_1=true`; when they later open /nad in another tab
 * we check `everflow_tracked_offer_3`, find it missing, and fire a fresh
 * EF.click() for offer_id 3 — producing a distinct transaction_id for that
 * funnel.
 *
 * @returns {string} Funnel-specific cookie name (e.g. 'everflow_tracked_offer_1',
 *   'everflow_tracked_offer_3', 'everflow_tracked_offer_23')
 */
const getEverflowTrackingCookieName = () => {
  const { organicOfferId } = getEverflowLandingConfig()
  return `everflow_tracked_offer_${organicOfferId}`
}

/**
 * Funnel-specific cookie name for the site-side mirror of the EF transaction_id.
 *
 * Each funnel (offer_id) maintains its own `site_ef_transaction_id_offer_<id>`
 * cookie so that switching between /cv-1 and /nad in the same browser produces
 * independent transaction IDs per funnel rather than overwriting a single
 * shared cookie.
 *
 * @returns {string} e.g. 'site_ef_transaction_id_offer_1',
 *   'site_ef_transaction_id_offer_3', 'site_ef_transaction_id_offer_23'
 */
const getSiteTransactionIdCookieName = () => {
  const { organicOfferId } = getEverflowLandingConfig()
  return `site_ef_transaction_id_offer_${organicOfferId}`
}

/**
 * Set Everflow tracking cookie
 * Creates a session cookie that persists across tabs but is deleted when browser window closes
 * Cookie is shared across .sunlight.com subdomain
 * Uses funnel-specific cookie name to allow independent tracking per funnel
 * @param {string} cookieName - Name of the cookie to set (defaults to funnel-specific name)
 * @param {string} cookieValue - Value to store in the cookie (default: 'true')
 */
const setEverflowTrackingCookie = (cookieName = null, cookieValue = 'true') => {
  const finalCookieName = cookieName || getEverflowTrackingCookieName()
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const domainPart = isLocalhost ? '' : '; domain=.sunlight.com'
  const securePart = window.location.protocol === 'https:' ? '; Secure' : ''

  // Session cookie (no expires/max-age) - deleted when browser window closes
  document.cookie = `${finalCookieName}=${cookieValue}; path=/${domainPart}; SameSite=Lax${securePart}`
  
  logger.debug(`🍪 Everflow tracking session cookie set: ${finalCookieName}`)
}

/**
 * Read the transaction_id directly from the Everflow SDK's own cookie
 * (ef_tid_c_a_<affiliate_id> or ef_tid_c_o_<offer_id>). This is the
 * authoritative value Everflow uses for attribution, so we use it as the
 * source of truth for site_ef_transaction_id rather than the value returned
 * by EF.click(), which can differ when multiple clicks are chained.
 *
 * When the user has visited multiple funnels in the same browser, the SDK
 * may have written cookies for several offers (e.g. `ef_tid_c_o_1` AND
 * `ef_tid_c_o_3`). We prefer the cookie that matches the current funnel's
 * organic offer_id so each funnel gets its own transaction_id mirrored
 * into `site_ef_transaction_id_offer_<id>`.
 *
 * @returns {string|null} The SDK-stored transaction_id for the current
 *   funnel, or null if not yet written.
 */
const getEfSdkTransactionIdFromCookie = () => {
  if (typeof document === 'undefined' || !document.cookie) return null
  const rows = document.cookie.split('; ').filter(Boolean)

  const { organicOfferId } = getEverflowLandingConfig()
  const offerSpecificPrefix = `ef_tid_c_o_${organicOfferId}=`

  const readValue = (row) => {
    const separatorIndex = row.indexOf('=')
    const value = row.slice(separatorIndex + 1)
    return decodeURIComponent(value).split('|')[0] || null
  }

  // Prefer the SDK cookie that matches the current funnel's offer_id.
  const offerSpecific = rows.find((row) => row.startsWith(offerSpecificPrefix))
  if (offerSpecific) return readValue(offerSpecific)

  // Fall back to any ef_tid_c_a_* / ef_tid_c_o_* cookie (e.g. when the SDK
  // only wrote the affiliate-keyed variant).
  const anySdk = rows.find(
    (row) => row.startsWith('ef_tid_c_a_') || row.startsWith('ef_tid_c_o_')
  )
  if (!anySdk) return null
  return readValue(anySdk)
}

/**
 * Persist the funnel-specific `site_ef_transaction_id_offer_<id>` cookie so
 * it always matches the EF SDK's own tracking cookie (ef_tid_c_a_* /
 * ef_tid_c_o_*) for the current funnel.
 *
 * Behavior:
 *  - Polls for the SDK cookie (it may be written a tick after EF.click()
 *    resolves). When found, mirrors that value into
 *    site_ef_transaction_id_offer_<currentOfferId>.
 *  - If the SDK cookie never appears (e.g. on localhost where cross-domain
 *    cookie writes may be blocked), falls back to the transaction_id
 *    resolved by EF.click() so the funnel still has a usable id.
 *
 * @param {string} [fallbackTransactionId] - transaction_id resolved by EF.click()
 *   used only if the SDK cookie never appears.
 * @param {Object} [options]
 * @param {string|null} [options.staleTransactionId] - SDK value from before a
 *   re-click; treated as "not written yet" so the old click's id is not mirrored.
 * @param {Function} [options.onDone] - Called once the cookie is settled (or given up on).
 * @param {number} [attempt] - Internal retry counter.
 */
const persistSiteTransactionIdCookie = (fallbackTransactionId, options = {}, attempt = 0) => {
  const { staleTransactionId = null, onDone } = options
  const maxAttempts = 10
  const retryDelayMs = 200
  const siteCookieName = getSiteTransactionIdCookieName()
  const done = () => {
    if (typeof onDone === 'function') onDone()
  }

  const sdkTransactionId = getEfSdkTransactionIdFromCookie()
  if (sdkTransactionId && sdkTransactionId !== staleTransactionId) {
    logger.debug(`✅ Everflow transaction ID (matched SDK cookie) for ${siteCookieName}:`, sdkTransactionId)
    setEverflowTrackingCookie(siteCookieName, sdkTransactionId)
    done()
    return
  }

  if (attempt < maxAttempts) {
    setTimeout(
      () => persistSiteTransactionIdCookie(fallbackTransactionId, options, attempt + 1),
      retryDelayMs
    )
    return
  }

  if (fallbackTransactionId) {
    logger.warn(
      `⚠️ EF SDK cookie not found after retries, falling back to EF.click() transaction_id for ${siteCookieName}`
    )
    setEverflowTrackingCookie(siteCookieName, fallbackTransactionId)
    done()
    return
  }

  logger.warn(`⚠️ Everflow transaction cookie not found after retries for ${siteCookieName}`)
  done()
}

/**
 * Get Everflow tracking cookie
 * @param {string} cookieName - Name of the cookie to retrieve (defaults to funnel-specific name)
 * @returns {boolean} True if tracking cookie exists and is 'true'
 */
const getEverflowTrackingCookie = (cookieName = null) => {
  const finalCookieName = cookieName || getEverflowTrackingCookieName()
  logger.debug(`🍪 Checking for Everflow tracking cookie: ${finalCookieName}`)
  
  if (typeof document === 'undefined' || !document.cookie) {
    logger.debug(`🍪 Cookie check failed: document or cookies unavailable`)
    return false
  }
  
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${finalCookieName}=`))
  
  if (cookie) {
    const value = cookie.split('=')[1]
    const result = value === 'true'
    logger.debug(`🍪 Cookie found: ${finalCookieName}=${value}, returning: ${result}`)
    return value
  }
  
  logger.debug(`🍪 Cookie not found: ${finalCookieName}, returning: false`)
  return false
}

/**
 * Clear Everflow tracking cookie by expiring it.
 *
 * Both the apex-domain and host-only scopes are expired. Our own cookies are
 * written on `.sunlight.com`, but the EF SDK's `ef_tid_c_*` cookies are
 * host-only unless the click configured a `tld`, and a `domain=.sunlight.com`
 * expiry does not remove a host-only cookie.
 *
 * @param {string} cookieName - Name of the cookie to clear (defaults to funnel-specific name)
 */
const clearEverflowTrackingCookie = (cookieName = null) => {
  const finalCookieName = cookieName || getEverflowTrackingCookieName()
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const securePart = window.location.protocol === 'https:' ? '; Secure' : ''
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 UTC'

  document.cookie = `${finalCookieName}=; ${expiry}; path=/; SameSite=Lax${securePart}`
  if (!isLocalhost) {
    document.cookie = `${finalCookieName}=; ${expiry}; path=/; domain=.sunlight.com; SameSite=Lax${securePart}`
  }

  logger.debug(`🗑️ Everflow tracking cookie cleared: ${finalCookieName}`)
}

const isEverflowSdkReady = () =>
  typeof window.EF !== 'undefined' &&
  typeof window.EF.click === 'function' &&
  typeof window.EF.configure === 'function'

/** Guards against overlapping EF.click() runs while the transaction cookie poll is pending. */
let everflowClickInFlight = false

/** Caps how many times a session may re-fire EF.click() to recover a missing transaction_id. */
const EF_CLICK_RECOVERY_MAX_ATTEMPTS = 3
const EF_CLICK_RECOVERY_KEY_PREFIX = 'ef_click_recovery_attempts_offer_'

const readClickRecoveryAttempts = (offerId) => {
  try {
    return parseInt(sessionStorage.getItem(`${EF_CLICK_RECOVERY_KEY_PREFIX}${offerId}`) || '0', 10) || 0
  } catch (_e) {
    return EF_CLICK_RECOVERY_MAX_ATTEMPTS
  }
}

const incrementClickRecoveryAttempts = (offerId) => {
  try {
    const next = readClickRecoveryAttempts(offerId) + 1
    sessionStorage.setItem(`${EF_CLICK_RECOVERY_KEY_PREFIX}${offerId}`, String(next))
  } catch (_e) {
    // sessionStorage unavailable (private mode / SSR) – recovery stays best-effort.
  }
}

/**
 * Decide whether a session that already carries `everflow_tracked_offer_<id>`
 * still needs a click.
 *
 * A session can be marked as tracked while `site_ef_transaction_id_offer_<id>`
 * is missing — e.g. the page unloaded mid-poll, or EF.click() resolved with no
 * transaction_id. Without this check the marker suppresses every later click
 * and the funnel runs the whole session with no transaction_id.
 *
 * Recovery order: mirror the EF SDK cookie if it exists, otherwise drop the
 * marker so the caller re-fires EF.click() (bounded per session).
 *
 * @returns {boolean} True when a fresh click should be performed.
 */
const needsClickAfterTrackedMarker = () => {
  const siteCookieName = getSiteTransactionIdCookieName()
  if (getEverflowTrackingCookie(siteCookieName)) return false

  const sdkTransactionId = getEfSdkTransactionIdFromCookie()
  if (sdkTransactionId) {
    setEverflowTrackingCookie(siteCookieName, sdkTransactionId)
    logger.debug(`✅ Recovered ${siteCookieName} from SDK cookie without re-firing the click.`)
    return false
  }

  const { organicOfferId } = getEverflowLandingConfig()
  if (readClickRecoveryAttempts(organicOfferId) >= EF_CLICK_RECOVERY_MAX_ATTEMPTS) {
    logger.warn(`⚠️ ${siteCookieName} still missing after ${EF_CLICK_RECOVERY_MAX_ATTEMPTS} recovery clicks, giving up for this session.`)
    return false
  }

  logger.warn(`⚠️ ${siteCookieName} missing despite tracked marker — clearing marker and re-firing EF.click().`)
  incrementClickRecoveryAttempts(organicOfferId)
  clearEverflowTrackingCookie()
  return true
}

/**
 * True when click tracking should run: either the session was never tracked,
 * or it was marked tracked but left without a transaction_id cookie.
 */
const shouldPerformEverflowClick = (organicOfferId) =>
  !getEverflowTrackingCookie() || (needsResponseBpoReclick(organicOfferId) && !pendingResponseBpoReclicks.has(String(organicOfferId))) || needsClickAfterTrackedMarker()

/**
 * Initialize Everflow tracking
 * Loads the Everflow script and configures tracking (only once per session)
 */
export const initializeEverflow = () => {
  logger.debug('📊 Initializing Everflow tracking...')

  // Tag ZA organic sessions with ORGANICZA before click/conversion tracking.
  maybeTagOrganicZaCoupon()

  // Direct hits on /checkout/selection need a brand-new transaction_id, which
  // that path clicks for itself. Firing the normal click here too would mint a
  // second transaction for the same page load.
  //
  // Only the *click* is handed over. The SDK still has to be loaded below:
  // conversions call window.EF.conversion on this page, and the direct-hit path
  // returns early without loading it whenever it reuses an already-issued id.
  const directHitOwnsClick = Boolean(ensureDirectHitTransactionRefresh())
  if (directHitOwnsClick) {
    logger.debug('ℹ️ Direct-hit transaction refresh owns the Everflow click for this page load.')
  }

  const { scriptSrc, organicOfferId } = getEverflowLandingConfig()
  const runClickTracking = () => {
    // Ensure SDK methods are available before triggering click
    if (!isEverflowSdkReady()) {
      logger.warn('⚠️ EF library not loaded or missing methods.')
      return
    }

    logger.debug('✅ Everflow library loaded.')

    // Re-evaluate coupon after SDK can read EF.urlParameter affiliate values
    maybeTagOrganicZaCoupon()

    if (directHitOwnsClick) return

    // Only perform click tracking if not already done (or if a previous run
    // left us without a site_ef_transaction_id_offer_<id> cookie).
    if (shouldPerformEverflowClick(organicOfferId)) {
      performEverflowClick()
    }
  }

  // Check if script SDK is already loaded and ready
  if (isEverflowSdkReady()) {
    logger.debug('ℹ️ Everflow script already loaded.')

    if (directHitOwnsClick) return

    if (!shouldPerformEverflowClick(organicOfferId)) {
      logger.debug('ℹ️ Everflow configure & click already executed. Skipping...')
      return
    }

    // SDK ready but click not tracked yet - do it now
    performEverflowClick()
    return
  }

  // Check if script tag for this landing account already exists
  const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)
  if (existingScript) {
    logger.debug('ℹ️ Everflow script tag already exists, waiting for load...')
    existingScript.addEventListener('load', runClickTracking, { once: true })
    runClickTracking()
    return
  }

  // Create script element for Everflow
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = scriptSrc
  
  script.onload = runClickTracking

  script.onerror = function() {
    logger.error('❌ Failed to load Everflow script')
  }

  // Append script to DOM
  document.head.appendChild(script)
}

/**
 * Finalize click tracking after EF.click() completes:
 *  - Mirrors the EF SDK's own transaction cookie (ef_tid_c_a_* / ef_tid_c_o_*)
 *    into site_ef_transaction_id so the two are always in sync. Polls briefly
 *    in case the SDK writes the cookie a tick after the promise resolves.
 *  - Falls back to the EF.click() resolved transaction_id only if the SDK
 *    cookie never appears (e.g. on localhost where it can't be written).
 *  - Marks everflow_tracked so we don't re-fire clicks during the session.
 *
 * The `everflow_tracked_offer_<id>` marker is written ONLY after
 * `site_ef_transaction_id_offer_<id>` has actually been persisted. Marking the
 * session as tracked while the cookie poll is still in flight permanently
 * suppressed the click on every later page load, leaving the funnel with no
 * transaction_id at all for the rest of the session.
 *
 * @param {string} [transactionIdFromPromise] - transaction_id resolved by EF.click()
 * @param {Object} [options]
 * @param {number|string} [options.clickAffiliateId] - affiliate_id the click was sent with
 * @param {string|null} [options.staleTransactionId] - SDK transaction_id from before a re-click
 */
const finalizeEverflowClick = (transactionIdFromPromise, options = {}) => {
  const { clickAffiliateId, staleTransactionId = null } = options
  const { organicOfferId } = getEverflowLandingConfig()
  persistSiteTransactionIdCookie(transactionIdFromPromise, {
    staleTransactionId,
    onDone: () => pendingResponseBpoReclicks.delete(String(organicOfferId))
  })
  setEverflowTrackingCookie()
  if (clickAffiliateId != null) {
    setEverflowTrackingCookie(getClickAffiliateCookieName(organicOfferId), String(clickAffiliateId))
  }
  logger.debug('✅ Everflow click tracking completed.')
}

/**
 * Determine if the current page belongs to the NAD funnel.
 * NAD routes: /nad, /nad/checkout/..., and /payment/return/nad.
 * @returns {boolean} True when the current pathname is part of the NAD funnel.
 */
const isNadFunnel = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  const pathname = window.location.pathname || ''
  return pathname.startsWith('/nad') || pathname.startsWith('/payment/return/nad')
}

/**
 * Determine if the current page belongs to the Sermorelin funnel.
 * Sermorelin routes: /sermorelin, /sermorelin/checkout/..., and /payment/return/sermorelin.
 * @returns {boolean} True when the current pathname is part of the Sermorelin funnel.
 */
const isSermorelinFunnel = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  const pathname = window.location.pathname || ''
  return pathname.startsWith('/sermorelin') || pathname.startsWith('/payment/return/sermorelin')
}

/**
 * Determine if the current page belongs to the CV-2 funnel.
 * CV-2 routes: /cv-2, /cv-2/checkout/..., and /payment/return/cv2.
 * @returns {boolean} True when the current pathname is part of the CV-2 funnel.
 */
const isCv2Funnel = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  const pathname = window.location.pathname || ''
  return pathname.startsWith('/cv-2') || pathname.startsWith('/payment/return/cv2')
}

/**
 * Perform Everflow click tracking (internal helper)
 *
 * Mirrors the main sunlight.com site logic:
 *  1. Configure organic defaults
 *  2. If `aff_id2` is present, fire a Department Media EF-to-EF click first
 *     (tracking_domain: dhgdf7trk.com), then fire the main click using the
 *     returned transaction_id as sub5
 *  3. Else if `affid2` is present, fire a chained click first
 *     (tracking_domain: a2cgktrk.com), then fire the main click using the
 *     returned transaction_id as sub5
 *  4. Else, fire the main click directly
 */
const performEverflowClick = () => {
  if (!isEverflowSdkReady()) {
    logger.warn('⚠️ Cannot perform Everflow click - EF not loaded')
    return
  }

  // The everflow_tracked marker is only written once the transaction cookie
  // lands, so it can't dedupe clicks that are still in flight.
  if (everflowClickInFlight) {
    logger.debug('ℹ️ Everflow click already in flight, skipping duplicate.')
    return
  }
  everflowClickInFlight = true

  try {
    logger.debug('🔧 Running Everflow configure & click...')

    const { organicOfferId } = getEverflowLandingConfig()
    logger.debug(`📊 Everflow organic offer_id: ${organicOfferId}`)

    // Configure EF with organic defaults
    window.EF.configure({
      organic: {
        offer_id: organicOfferId,
        affiliate_id: 9
      }
    })

    // Determine environment (Live or Staging)
    const environment = config.testMode ? 'Staging' : 'Live'
    logger.debug('📊 Everflow environment:', environment)

    const efTrackingBase = usesAlternateEverflowTracker()
      ? EF_TRACKING_BASE_ALTERNATE
      : EF_TRACKING_BASE_DEFAULT

    // An earlier click for this offer exists, so this is a ZA re-click as Response BPO.
    const isResponseBpoReclick = !!getEverflowTrackingCookie()
    const staleTransactionId = isResponseBpoReclick ? getEfSdkTransactionIdFromCookie() : null
    if (isResponseBpoReclick) {
      pendingResponseBpoReclicks.add(String(organicOfferId))
      logger.debug(`🔁 Re-clicking offer_id=${organicOfferId} as Response BPO (ZA organic session)`)
    }

    const fireMainClick = (chainTransactionId) => {
      const clickOfferId = window.EF.urlParameter('oid') || String(organicOfferId)
      const clickAffiliateId = resolveClickAffiliateId(9)
      const finalizeOptions = { clickAffiliateId, staleTransactionId }

      const clickResult = window.EF.click({
        tracking_domain: efTrackingBase,
        offer_id: clickOfferId,
        affiliate_id: clickAffiliateId,
        source_id: window.EF.urlParameter('source_id'),
        sub1: window.EF.urlParameter('sub1'),
        sub2: window.EF.urlParameter('sub2'),
        sub3: window.EF.urlParameter('sub3'),
        sub4: window.EF.urlParameter('sub4'),
        sub5: chainTransactionId || window.EF.urlParameter('sub5'),
        uid: window.EF.urlParameter('uid'),
        fbclid: window.EF.urlParameter('fbclid'),
        gclid: window.EF.urlParameter('gclid'),
        transaction_id: window.EF.urlParameter('_ef_transaction_id'),
        adv2: environment
      })

      if (clickResult && typeof clickResult.then === 'function') {
        clickResult
          .then((transactionId) => finalizeEverflowClick(transactionId, finalizeOptions))
          .catch((err) => {
            logger.warn('⚠️ Everflow main click promise rejected.', err)
            // Still try to persist whatever EF wrote, then mark as tracked
            finalizeEverflowClick(undefined, finalizeOptions)
          })
      } else {
        // Older SDK shapes that don't return a thenable: poll for the cookie
        finalizeEverflowClick(undefined, finalizeOptions)
        logger.debug('✅ Everflow click tracking completed (no promise).')
      }
    }

    /**
     * Run a chained EF.click() (e.g. Department Media or generic chained
     * affid2 partner) and then fire the main click, forwarding the chained
     * transaction_id as sub5.
     *
     * Defensive against three real-world failure modes that previously caused
     * `site_ef_transaction_id` to never get set:
     *   1. SDK returns undefined / non-thenable → call .then on undefined throws
     *   2. Promise hangs (e.g. blocked tracking domain on localhost) → main
     *      click never fires. We add a timeout safety net.
     *   3. Synchronous throw inside EF.click → swallowed by outer try/catch.
     *
     * The main click is guaranteed to fire exactly once.
     */
    const fireChainedThenMain = ({ label, clickConfig, onChainResolved }) => {
      let mainFired = false
      const fireMainOnce = (chainTransactionId) => {
        if (mainFired) return
        mainFired = true
        fireMainClick(chainTransactionId)
      }

      let chainResult
      try {
        chainResult = window.EF.click(clickConfig)
      } catch (err) {
        logger.warn(`⚠️ Everflow ${label} click threw, firing main click as fallback.`, err)
        fireMainOnce()
        return
      }

      // Safety net: ensure the main click always fires even if the chained
      // tracking domain hangs (common on localhost or when blocked).
      const timeoutId = setTimeout(() => {
        logger.warn(`⚠️ Everflow ${label} click timed out, firing main click as fallback.`)
        fireMainOnce()
      }, 3000)

      if (chainResult && typeof chainResult.then === 'function') {
        chainResult
          .then((transactionId) => {
            clearTimeout(timeoutId)
            if (typeof onChainResolved === 'function') {
              try { onChainResolved(transactionId) } catch (e) {
                logger.warn(`⚠️ Everflow ${label} onChainResolved threw.`, e)
              }
            }
            fireMainOnce(transactionId)
          })
          .catch((err) => {
            clearTimeout(timeoutId)
            logger.warn(`⚠️ Everflow ${label} click failed, firing main click as fallback.`, err)
            fireMainOnce()
          })
      } else {
        // SDK returned no promise — fire main click immediately without a
        // chained transaction_id rather than dropping it on the floor.
        clearTimeout(timeoutId)
        logger.debug(`ℹ️ Everflow ${label} click did not return a promise, firing main click without chained transaction_id.`)
        fireMainOnce()
      }
    }

    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'

    // 1) Department Media EF-to-EF direct linking (aff_id2)
    //    Their Transaction ID is forwarded as sub5 on the main click.
    if (window.EF.urlParameter('aff_id2')) {
      logger.debug('🔗 Everflow aff_id2 (Department Media) found in url')
      // Persist `aff_id2` so subsequent funnel pages (where the URL no longer
      // carries it) still recognize this as a Department Media session.
      getOrPersistAffId2()
      fireChainedThenMain({
        label: 'Department Media',
        clickConfig: {
          tracking_domain: 'https://www.dhgdf7trk.com',
          offer_id: window.EF.urlParameter('oid2'),
          affiliate_id: window.EF.urlParameter('aff_id2'),
          sub1: window.EF.urlParameter('sub1'),
          sub2: window.EF.urlParameter('sub2'),
          sub3: window.EF.urlParameter('sub3'),
          sub4: window.EF.urlParameter('sub4'),
          sub5: window.EF.urlParameter('sub5')
        },
        onChainResolved: () => {
          // On localhost, configuring tld to sunlight.com prevents the main
          // click from writing its cookies (browser blocks cross-domain
          // cookie writes), so skip it there.
          if (!isLocalhost) {
            window.EF.configure({ tld: 'sunlight.com' })
          }
        }
      })
      return
    }

    // 2) Chained click logic (affid2)
    if (window.EF.urlParameter('affid2')) {
      logger.debug('🔗 Everflow affid2 (chained click) found in url')
      fireChainedThenMain({
        label: 'chained',
        clickConfig: {
          tracking_domain: 'https://www.a2cgktrk.com',
          offer_id: window.EF.urlParameter('oid2'),
          affiliate_id: window.EF.urlParameter('affid2')
        }
      })
      return
    }

    // 3) Standard click registration
    fireMainClick()
  } catch (error) {
    everflowClickInFlight = false
    logger.error('❌ Error performing Everflow click:', error)
  }
}

// ---------------------------------------------------------------------------
// Direct hits on /checkout/selection/<caseId> (and /checkout/payment/<caseId>,
// which every funnel redirects to selection)
//
// A user who opens a resume link (email/SMS) lands straight on the selection
// page without walking the quiz in this page load. That is a new session and
// needs its own transaction_id, so the stale one left over from an earlier
// visit must not be reused for the conversions that follow.
//
// Everflow cannot be asked to make that call for us: while its own
// `ef_tid_c_o_<offerId>` click cookie is alive it reuses the existing
// transaction, so a browser that has one would never produce a new id. We
// therefore clear this funnel's transaction cookies first, which forces the
// click to issue a fresh transaction.
//
// To keep that from happening more than once per link, the issued id is
// recorded in localStorage against the caseId. A refresh or a second tab on the
// same URL reuses it instead of forcing another transaction.
//
// Everflow conversions are held until the click settles, then replayed against
// the resulting id. Firing them earlier risks attributing them to the old click.
// ---------------------------------------------------------------------------

/**
 * Pathname captured when this module is first evaluated, i.e. the URL the
 * document was opened with. `window.location.pathname` is unreliable later
 * because react-router rewrites it during in-app navigation.
 */
const ENTRY_PATHNAME = typeof window !== 'undefined' ? window.location.pathname : ''

// Payment entries count too: every funnel redirects a direct hit on
// /checkout/payment/<caseId> to /checkout/selection/<caseId>.
const CHECKOUT_SELECTION_ENTRY_PATTERN = /\/checkout\/(?:selection|payment)\/([^/?#]+)/
const CHECKOUT_PAYMENT_ENTRY_PATTERN = /\/checkout\/payment\/([^/?#]+)/

/** How long to watch for Everflow to issue a different transaction_id after the click. */
const DIRECT_HIT_TRANSACTION_SETTLE_MS = 10000

/**
 * How long a direct-hit transaction is shared before a new visit forces a new
 * one. Tabs opened inside this window reuse the same transaction_id; a direct
 * hit after it counts as a new session.
 */
const DIRECT_HIT_TRANSACTION_REUSE_WINDOW_MS = 30 * 60 * 1000

const getDirectHitTransactionKey = (caseId) => `everflow_direct_hit_tid_${caseId}`

/**
 * transaction_id already issued for a direct hit on this case. Stored in
 * localStorage so every tab on the link shares it, but only honored inside
 * the reuse window.
 *
 * @returns {string} The reusable transaction_id, or '' when this direct hit
 *   should mint its own.
 */
const readDirectHitTransaction = (caseId) => {
  const storageKey = getDirectHitTransactionKey(caseId)
  const raw = localStore.getItem(storageKey)
  if (!raw) return ''

  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch {
    parsed = null
  }

  const transactionId = parsed?.tid || ''
  const issuedAt = Number(parsed?.ts) || 0

  // No timestamp means it was written by the earlier format, which never
  // expired. Treat it as a past visit so this hit mints a fresh transaction.
  if (!transactionId || !issuedAt) {
    localStore.removeItem(storageKey)
    return ''
  }

  if (Date.now() - issuedAt > DIRECT_HIT_TRANSACTION_REUSE_WINDOW_MS) {
    logger.debug(
      `⌛ Direct-hit transaction for case ${caseId} is outside the ${DIRECT_HIT_TRANSACTION_REUSE_WINDOW_MS / 60000}-minute reuse window — treating this as a new session`
    )
    localStore.removeItem(storageKey)
    return ''
  }

  return transactionId
}

/** Record the transaction issued for this direct hit, starting the reuse window. */
const rememberDirectHitTransaction = (caseId, transactionId) => {
  localStore.setItem(
    getDirectHitTransactionKey(caseId),
    JSON.stringify({ tid: transactionId, ts: Date.now() })
  )
}

/**
 * caseId captured from the entry URL by `pattern`.
 * @param {RegExp} pattern
 * @returns {string} caseId, or '' when the entry URL does not match.
 */
const getEntryCaseId = (pattern) => {
  const match = ENTRY_PATHNAME.match(pattern)
  if (!match) return ''
  try {
    return decodeURIComponent(match[1])
  } catch (_e) {
    return match[1]
  }
}

/**
 * caseId when the document was opened directly on a checkout selection URL
 * (or a payment URL, which redirects to selection).
 * @returns {string} caseId, or '' when the entry URL was any other page.
 */
const getDirectCheckoutSelectionCaseId = () => getEntryCaseId(CHECKOUT_SELECTION_ENTRY_PATTERN)

/**
 * True when this page load is a direct hit on the selection page — opened on
 * /checkout/selection/:caseId, or on /checkout/payment/:caseId (redirected to
 * selection). Abandoned-cart links can point at either.
 *
 * `hasInFlowBrowserCaseMatch` is the same signal the checkout guards use: it is
 * written on the contact-info → checkout navigation, so it is present when the
 * user walked the quiz in this browser (including across a page refresh) and
 * absent when they arrived from an external link. Skipping in-flow arrivals
 * keeps a mid-funnel refresh from re-clicking and potentially splitting the
 * session's attribution.
 */
export const isDirectCheckoutSelectionHit = () => {
  const caseId = getDirectCheckoutSelectionCaseId()
  if (!caseId) return false
  return !hasInFlowBrowserCaseMatch(caseId)
}

/**
 * True when this page load is a direct hit opened on /checkout/payment/:caseId
 * (callers redirect it to /checkout/selection/:caseId).
 */
export const isDirectCheckoutPaymentHit = () =>
  Boolean(getEntryCaseId(CHECKOUT_PAYMENT_ENTRY_PATTERN)) && isDirectCheckoutSelectionHit()

/** Resolves once the EF SDK is usable, or false if it never loads. */
const ensureEverflowSdkLoaded = ({ maxAttempts = 50, delayMs = 200 } = {}) =>
  new Promise((resolve) => {
    if (isEverflowSdkReady()) {
      resolve(true)
      return
    }

    const { scriptSrc } = getEverflowLandingConfig()
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      logger.debug('📥 Loading Everflow SDK for direct-hit transaction refresh')
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = scriptSrc
      document.head.appendChild(script)
    }

    const poll = (attempt) => {
      if (isEverflowSdkReady()) {
        resolve(true)
        return
      }
      if (attempt >= maxAttempts) {
        resolve(false)
        return
      }
      setTimeout(() => poll(attempt + 1), delayMs)
    }
    poll(0)
  })

/**
 * Watch this funnel's transaction_id for a value different from the one the
 * click started with. Resolving empty means the click never produced a new
 * transaction within the settle window.
 *
 * @param {string|number} offerId
 * @param {string} previousTransactionId
 * @returns {Promise<string>} the newly issued transaction_id, or '' on timeout
 */
const waitForChangedTransactionId = (
  offerId,
  previousTransactionId,
  { settleMs = DIRECT_HIT_TRANSACTION_SETTLE_MS, delayMs = 200 } = {}
) =>
  new Promise((resolve) => {
    const deadline = Date.now() + settleMs
    const poll = () => {
      const transactionId = getEverflowParams(offerId)?.transaction_id || ''
      if (transactionId && transactionId !== previousTransactionId) {
        resolve(transactionId)
        return
      }
      if (Date.now() >= deadline) {
        resolve('')
        return
      }
      setTimeout(poll, delayMs)
    }
    poll()
  })

/**
 * Force a new Everflow transaction for a direct hit, or reuse the one already
 * issued for this case.
 *
 * @param {string} caseId
 * @returns {Promise<string>} the transaction_id this session should use
 */
const refreshEverflowTransactionForDirectHit = async (caseId) => {
  const { organicOfferId } = getEverflowLandingConfig()
  const siteCookieName = getSiteTransactionIdCookieName()
  const previousTransactionId =
    getEfSdkTransactionIdFromCookie() || getEverflowTrackingCookie(siteCookieName) || ''

  // Another tab — or an earlier load of this same link — already forced a
  // transaction for this case. Reuse it so every tab on the link shares one.
  const alreadyIssued = readDirectHitTransaction(caseId)
  if (alreadyIssued) {
    if (getEverflowTrackingCookie(siteCookieName) !== alreadyIssued) {
      setEverflowTrackingCookie(siteCookieName, alreadyIssued)
      logger.debug(`🔁 Restored the direct-hit transaction_id for case ${caseId}:`, alreadyIssued)
    } else {
      logger.debug(`ℹ️ Direct hit on case ${caseId} already has transaction_id ${alreadyIssued} — reusing it`)
    }
    return alreadyIssued
  }

  logger.debug(
    `🔄 Direct hit on checkout selection (case ${caseId}) — forcing a new Everflow transaction for offer_id=${organicOfferId}`,
    { previousTransactionId: previousTransactionId || 'none' }
  )

  const sdkReady = await ensureEverflowSdkLoaded()
  if (!sdkReady) {
    logger.warn('⚠️ Everflow SDK never loaded — keeping the stored transaction_id for this direct hit')
    return previousTransactionId
  }

  // Re-evaluate now that EF.urlParameter can read affiliate values, matching
  // what initializeEverflow() does once the SDK is ready.
  maybeTagOrganicZaCoupon()

  // Everflow reuses the existing transaction while its own click cookie is
  // alive, so it has to go for the click to issue a new one. The tracked
  // marker goes too, otherwise it would suppress later clicks in this session.
  clearEverflowTrackingCookie(`ef_tid_c_o_${organicOfferId}`)
  clearEverflowTrackingCookie(siteCookieName)
  clearEverflowTrackingCookie(getEverflowTrackingCookieName())

  // A click may already be in flight from initializeEverflow(); this one
  // deliberately supersedes it, so drop the duplicate-click guard.
  everflowClickInFlight = false
  performEverflowClick()

  const issuedTransactionId = await waitForChangedTransactionId(organicOfferId, previousTransactionId)

  if (!issuedTransactionId) {
    // Put the old id back rather than leaving the funnel with no transaction
    // at all — an imperfect attribution still beats an Invalid conversion.
    logger.warn(
      `⚠️ Direct-hit click issued no new transaction_id; restoring ${previousTransactionId || '(none)'}`
    )
    if (previousTransactionId) {
      setEverflowTrackingCookie(siteCookieName, previousTransactionId)
    }
    return previousTransactionId
  }

  // persistSiteTransactionIdCookie() runs as part of the click and mirrors
  // whatever the SDK cookie held at that moment, which can still be a stale
  // affiliate-keyed value when the new one lands a beat later. Correct it here.
  setEverflowTrackingCookie(siteCookieName, issuedTransactionId)
  rememberDirectHitTransaction(caseId, issuedTransactionId)

  logger.debug(`✅ Forced a new Everflow transaction_id for case ${caseId}:`, issuedTransactionId)
  return issuedTransactionId
}

/** Conversions queued while the direct-hit click is settling. */
let heldConversions = []
let isHoldingConversions = false
let directHitRefreshPromise = null
let directHitTransactionId = ''

const releaseHeldConversions = (transactionId) => {
  const { organicOfferId } = getEverflowLandingConfig()
  const queued = heldConversions
  heldConversions = []
  isHoldingConversions = false

  if (!queued.length) return

  logger.debug(`▶️ Releasing ${queued.length} held Everflow conversion(s) after the direct-hit click`)
  queued.forEach((conversionData) => {
    // Callers resolved transaction_id before the click settled. Re-point them
    // at the winning id, except for cross-funnel payloads that deliberately
    // target another offer's transaction.
    const targetsCurrentOffer =
      conversionData.offer_id == null ||
      String(conversionData.offer_id) === String(organicOfferId)

    trackConversion(
      transactionId && targetsCurrentOffer
        ? { ...conversionData, transaction_id: transactionId }
        : conversionData
    )
  })
}

/**
 * Fire the direct-hit Everflow click if this page load needs one.
 *
 * Idempotent, so it runs at most once per page load no matter how many callers
 * ask. The conversion hold is set synchronously so a conversion firing in the
 * same tick is queued rather than attributed before the click settles.
 *
 * @returns {Promise<string>|null} Resolves with the transaction_id this session
 *   should use, or null when this page load is not a direct hit.
 */
export const ensureDirectHitTransactionRefresh = () => {
  if (directHitRefreshPromise) return directHitRefreshPromise
  if (typeof window === 'undefined') return null
  if (!isDirectCheckoutSelectionHit()) return null

  isHoldingConversions = true

  const caseId = getDirectCheckoutSelectionCaseId()
  directHitRefreshPromise = refreshEverflowTransactionForDirectHit(caseId)
    .catch((error) => {
      logger.error('❌ Direct-hit Everflow click failed:', error)
      return ''
    })
    .then((transactionId) => {
      directHitTransactionId = transactionId
      releaseHeldConversions(transactionId)
      return transactionId
    })

  return directHitRefreshPromise
}

/**
 * transaction_id resolved by the direct-hit click, for callers that persist
 * `ef_tid` (FunnelContext, funnel-get-data merge).
 *
 * @returns {string} The id this session should use, or '' when this page load
 *   is not a direct hit or the click has not settled yet — in both cases stored
 *   values should be left as they are.
 */
export const getDirectHitTransactionId = () => {
  ensureDirectHitTransactionRefresh()
  return directHitTransactionId
}

/**
 * Force-fire a single EF.click() for a specific offer_id, regardless of the
 * funnel the user is currently on. Designed for cross-funnel upsells such as
 * the CV-1 NAD+ upsell page (e.g. `.../payment-successful-nad-upsell-repayment`)
 * which lives on a CV-1 path but represents a purchase attributed to the
 * NAD offer (offer_id 3). Without this, the page would only have a CV-1
 * (offer_id 1) transaction_id available and any NAD conversion fired from
 * it would not be attributable to a fresh NAD click.
 *
 * Behavior:
 *  - Loads the Everflow SDK if it isn't already loaded.
 *  - Session-deduped via the `everflow_tracked_offer_<offerId>` cookie so we
 *    don't re-fire on component remounts or back-navigation.
 *  - Fires `EF.click({ offer_id, affiliate_id, ... })` and mirrors the
 *    resulting transaction_id into `site_ef_transaction_id_offer_<offerId>`
 *    so subsequent conversion helpers (`getEverflowParams(offerId)`,
 *    `getEverflowTransactionIdsByOffer()`) can read it.
 *
 * @param {number|string} offerId - The Everflow offer_id to register the
 *   click against (e.g. 3 for NAD+).
 * @param {Object} [options]
 * @param {number|string} [options.affiliateId=9] - The affiliate_id to send
 *   with the click.
 */
const readSdkTransactionIdForOffer = (offerIdStr) => {
  if (typeof document === 'undefined') return null
  const row = (document.cookie || '')
    .split('; ')
    .filter(Boolean)
    .find((r) => r.startsWith(`ef_tid_c_o_${offerIdStr}=`))
  if (!row) return null
  const sep = row.indexOf('=')
  return decodeURIComponent(row.slice(sep + 1)).split('|')[0] || null
}

/** Poll options for upsell pages waiting for per-offer transaction_id (10s). */
export const UPSELL_EF_TRANSACTION_WAIT = { maxAttempts: 50, delayMs: 200 }

/**
 * Delay after upsell EF.click() before firing advanced events (product/plan selection).
 * Everflow often drops the first conversion if it fires in the same window as the
 * click cookie appearing; plan selection worked because it was delayed 1s later.
 */
export const UPSELL_EF_CONVERSION_SETTLE_MS = 1000

export const createEverflowTransactionForOffer = (offerId, options = {}) => {
  const { affiliateId = 9 } = options
  const offerIdStr = String(offerId)
  const dedupeCookieName = `everflow_tracked_offer_${offerIdStr}`
  const siteCookieName = `site_ef_transaction_id_offer_${offerIdStr}`

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  if (pendingResponseBpoReclicks.has(offerIdStr)) {
    logger.debug(`ℹ️ Response BPO re-click for offer_id=${offerIdStr} already in flight, skipping`)
    return
  }

  // Session dedupe – don't re-fire if we already tracked a click for this offer
  // (unless a ZA organic session's click for it was not sent as Response BPO).
  const isResponseBpoReclick =
    !!getEverflowTrackingCookie(dedupeCookieName) && needsResponseBpoReclick(offerIdStr)
  if (getEverflowTrackingCookie(dedupeCookieName) && !isResponseBpoReclick) {
    logger.debug(`ℹ️ Everflow click for offer_id=${offerIdStr} already tracked this session, skipping new click`)
    const sdkTransactionId = readSdkTransactionIdForOffer(offerIdStr)
    const existingSite = getEverflowTrackingCookie(siteCookieName)
    if (sdkTransactionId && !existingSite) {
      setEverflowTrackingCookie(siteCookieName, sdkTransactionId)
      logger.debug(`✅ Synced ${siteCookieName} from SDK cookie (dedupe skip path)`)
    }
    return
  }

  const efTrackingBase = usesAlternateEverflowTracker()
      ? EF_TRACKING_BASE_ALTERNATE
      : EF_TRACKING_BASE_DEFAULT

  // Poll briefly for the SDK cookie (ef_tid_c_o_<offerId>) after EF.click()
  // resolves. The SDK is authoritative — its cookie is in the canonical
  // `transaction_id|sub1|sub2|...` pipe-delimited format, so position [0]
  // is the real transaction_id. EF.click()'s own promise can resolve to a
  // chained/previous transaction_id (e.g. sub1) rather than the
  // freshly-issued one, so we MUST NOT use it as the source of truth.
  const staleTransactionId = isResponseBpoReclick ? readSdkTransactionIdForOffer(offerIdStr) : null
  if (isResponseBpoReclick) {
    pendingResponseBpoReclicks.add(offerIdStr)
    logger.debug(`🔁 Re-clicking offer_id=${offerIdStr} as Response BPO (ZA organic session)`)
  }
  let clickAffiliateId = null

  const markClicked = () => {
    setEverflowTrackingCookie(dedupeCookieName, 'true')
    if (clickAffiliateId != null) {
      setEverflowTrackingCookie(getClickAffiliateCookieName(offerIdStr), String(clickAffiliateId))
    }
    pendingResponseBpoReclicks.delete(offerIdStr)
  }

  const finalize = (transactionIdFromPromise, attempt = 0) => {
    const maxAttempts = 10
    const retryDelayMs = 200

    const sdkTransactionId = readSdkTransactionIdForOffer(offerIdStr)
    if (sdkTransactionId && sdkTransactionId !== staleTransactionId) {
      setEverflowTrackingCookie(siteCookieName, sdkTransactionId)
      markClicked()
      logger.debug(`✅ Persisted ${siteCookieName}=${sdkTransactionId} (from SDK cookie)`)
      return
    }

    if (attempt < maxAttempts) {
      setTimeout(() => finalize(transactionIdFromPromise, attempt + 1), retryDelayMs)
      return
    }

    // SDK cookie never appeared (e.g. localhost or blocked tracking domain).
    // Fall back to the promise value only as a last resort; warn loudly so
    // the mismatch with EF dashboards is debuggable.
    if (transactionIdFromPromise) {
      logger.warn(`⚠️ EF SDK cookie ef_tid_c_o_${offerIdStr} not found after ${maxAttempts} retries; falling back to EF.click() promise value (may not match Everflow's record)`, transactionIdFromPromise)
      setEverflowTrackingCookie(siteCookieName, transactionIdFromPromise)
    } else {
      logger.warn(`⚠️ EF.click() for offer_id=${offerIdStr} produced no transaction_id and no SDK cookie was written`)
    }
    markClicked()
  }

  const fireClick = () => {
    if (!isEverflowSdkReady()) {
      logger.warn(`⚠️ Cannot fire EF.click() for offer_id=${offerIdStr} – SDK not ready`)
      return
    }

    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'

    try {
      const environment = config.testMode ? 'Staging' : 'Live'

      // Re-configure the EF SDK for the target offer BEFORE firing the click.
      // Without this, the SDK still has the path-based organic context from
      // `initializeEverflow()` (e.g. offer_id 1 on /cv-1) and the cross-funnel
      // click for offer 3 may be silently de-duped server-side or recorded
      // against the wrong offer. We also set `tld: 'sunlight.com'` (non-local)
      // so the click writes its cookies on the apex domain — same pattern used
      // by the chained Department Media click in `performEverflowClick()`.
      try {
        window.EF.configure({
          organic: {
            offer_id: offerIdStr,
            affiliate_id: 9
          }
        })
        if (!isLocalhost) {
          window.EF.configure({ tld: 'sunlight.com' })
        }
        logger.debug(`🔧 EF.configure() set organic offer_id=${offerIdStr}, affiliate_id=${affiliateId}` + (isLocalhost ? '' : ', tld=sunlight.com'))
      } catch (configErr) {
        logger.warn(`⚠️ EF.configure() for offer_id=${offerIdStr} threw`, configErr)
      }
      clickAffiliateId = resolveClickAffiliateId(affiliateId)
      const clickPayload = {
        tracking_domain: efTrackingBase,
        offer_id: offerIdStr,
        affiliate_id: clickAffiliateId,
        source_id: window.EF.urlParameter('source_id'),
        sub1: window.EF.urlParameter('sub1'),
        sub2: window.EF.urlParameter('sub2'),
        sub3: window.EF.urlParameter('sub3'),
        sub4: window.EF.urlParameter('sub4'),
        sub5: window.EF.urlParameter('sub5'),
        uid: window.EF.urlParameter('uid'),
        fbclid: window.EF.urlParameter('fbclid'),
        gclid: window.EF.urlParameter('gclid'),
        transaction_id: window.EF.urlParameter('_ef_transaction_id'),
        adv2: environment
      }

      logger.debug(`📊 Firing cross-funnel EF.click() for offer_id=${offerIdStr}`, clickPayload)

      const clickResult = window.EF.click(clickPayload)

      if (clickResult && typeof clickResult.then === 'function') {
        clickResult
          .then((promiseValue) => {
            // NOTE: `promiseValue` is NOT guaranteed to be the freshly-issued
            // transaction_id — it can be a chained/previous value (e.g. sub1).
            // `finalize()` reads the SDK's own cookie as the source of truth.
            logger.debug(`✅ EF.click() resolved for offer_id=${offerIdStr} (promise value, may differ from real transaction_id):`, promiseValue)
            finalize(promiseValue)
          })
          .catch((err) => {
            logger.warn(`⚠️ EF.click() for offer_id=${offerIdStr} rejected`, err)
            finalize()
          })
      } else {
        logger.debug(`ℹ️ EF.click() for offer_id=${offerIdStr} returned non-thenable, polling SDK cookie`, clickResult)
        finalize()
      }
    } catch (error) {
      logger.error(`❌ Error firing EF.click() for offer_id=${offerIdStr}:`, error)
    }
  }

  if (isEverflowSdkReady()) {
    fireClick()
    return
  }

  // Reuse an existing script tag if present; otherwise inject our own.
  const existingScript = document.querySelector(`script[src="${EF_SCRIPT_DEFAULT}"]`)
  if (existingScript) {
    logger.debug(`ℹ️ Waiting for existing Everflow script to load before firing offer_id=${offerIdStr} click`)
    existingScript.addEventListener('load', fireClick, { once: true })
    if (isEverflowSdkReady()) fireClick()
    return
  }

  logger.debug(`📥 Loading Everflow SDK to fire cross-funnel offer_id=${offerIdStr} click`)
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = EF_SCRIPT_DEFAULT
  script.onload = fireClick
  script.onerror = () => {
    logger.error(`❌ Failed to load Everflow script for cross-funnel offer_id=${offerIdStr} click`)
  }
  document.head.appendChild(script)
}

/**
 * Poll until the per-offer Everflow transaction cookie is available.
 * Used by cross-funnel upsell pages after `createEverflowTransactionForOffer()`.
 *
 * @param {number|string} offerId
 * @param {Object} [options]
 * @param {number} [options.maxAttempts=25]
 * @param {number} [options.delayMs=200]
 * @returns {Promise<string>} transaction_id or empty string if timed out
 */
export const waitForEverflowOfferTransactionId = (offerId, { maxAttempts = 50, delayMs = 200 } = {}) => {
  return new Promise((resolve) => {
    const poll = (attempt) => {
      const params = getEverflowParams(offerId)
      const transactionId = params?.transaction_id || ''
      if (transactionId) {
        resolve(transactionId)
        return
      }
      if (attempt >= maxAttempts) {
        logger.warn(`⚠️ Timed out waiting for Everflow transaction_id for offer_id=${offerId}`)
        resolve('')
        return
      }
      setTimeout(() => poll(attempt + 1), delayMs)
    }
    poll(0)
  })
}

/**
 * Wait for the current funnel's organic Everflow click cookie.
 * Needed when checkout is opened in a fresh browser (no prior click cookie)
 * while `initializeEverflow()` is still writing one.
 *
 * @param {number|string|null} [offerId]
 * @param {Object} [options]
 * @returns {Promise<string>}
 */
export const waitForCurrentEverflowTransactionId = (offerId = null, options = {}) => {
  const resolvedOfferId = offerId ?? getEverflowLandingConfig().organicOfferId
  return waitForEverflowOfferTransactionId(resolvedOfferId, options)
}

/** In-flight recovery clicks keyed by offer_id, so parallel conversions share one click. */
const transactionRecoveryInFlight = new Map()

/**
 * Resolve a transaction_id for an offer, firing a recovery EF.click() when the
 * browser turns out not to have one.
 *
 * Everflow rejects conversions sent without a transaction_id as Invalid, so an
 * event that reaches this point would otherwise be lost entirely. A click is
 * cheap by comparison, and it gives every later conversion in the session a
 * transaction to attach to.
 *
 * Concurrent callers share a single click, and recovery clicks are capped per
 * session so a genuinely blocked tracking domain can't cause one per event.
 *
 * @param {number|string|null} [offerId] - defaults to the current funnel's organic offer
 * @param {Object} [options]
 * @param {number} [options.waitAttempts=12] - 200ms polls spent waiting on an
 *   already-in-flight click before firing our own
 * @returns {Promise<string>} transaction_id, or '' when recovery did not produce one
 */
export const ensureEverflowTransactionId = (offerId = null, { waitAttempts = 12 } = {}) => {
  const resolvedOfferId = offerId ?? getEverflowLandingConfig().organicOfferId
  const offerKey = String(resolvedOfferId)

  const existing = getEverflowParams(resolvedOfferId)?.transaction_id || ''
  if (existing) return Promise.resolve(existing)

  const inFlight = transactionRecoveryInFlight.get(offerKey)
  if (inFlight) return inFlight

  const recovery = (async () => {
    // The click from initializeEverflow() may simply not have landed yet.
    const waited = await waitForEverflowOfferTransactionId(resolvedOfferId, {
      maxAttempts: waitAttempts,
      delayMs: 200,
    })
    if (waited) return waited

    if (readClickRecoveryAttempts(offerKey) >= EF_CLICK_RECOVERY_MAX_ATTEMPTS) {
      logger.warn(`⚠️ Recovery click limit reached for offer_id=${offerKey}; conversion will go out without a transaction_id`)
      return ''
    }

    logger.warn(`⚠️ No Everflow transaction_id for offer_id=${offerKey} — firing a recovery click`)
    incrementClickRecoveryAttempts(offerKey)

    const sdkReady = await ensureEverflowSdkLoaded()
    if (!sdkReady) {
      logger.error('❌ Everflow SDK unavailable — cannot recover a transaction_id')
      return ''
    }

    if (offerKey === String(getEverflowLandingConfig().organicOfferId)) {
      everflowClickInFlight = false
      performEverflowClick()
    } else {
      // The per-offer helper dedupes on this marker; we already know there is
      // no transaction behind it, so drop it before asking for a new click.
      clearEverflowTrackingCookie(`everflow_tracked_offer_${offerKey}`)
      createEverflowTransactionForOffer(resolvedOfferId)
    }

    const recovered = await waitForEverflowOfferTransactionId(resolvedOfferId, {
      maxAttempts: 25,
      delayMs: 200,
    })

    if (recovered) {
      logger.debug(`✅ Recovered Everflow transaction_id for offer_id=${offerKey}:`, recovered)
    } else {
      logger.error(`❌ Recovery click for offer_id=${offerKey} still produced no transaction_id`)
    }
    return recovered
  })().finally(() => {
    transactionRecoveryInFlight.delete(offerKey)
  })

  transactionRecoveryInFlight.set(offerKey, recovery)
  return recovery
}

/**
 * Track a conversion event with retry mechanism
 * @param {Object} conversionData - Conversion data to track
 * @param {number} retryCount - Number of retries (internal use)
 */
export const trackConversion = (conversionData = {}, retryCount = 0) => {
  const maxRetries = 5
  const retryDelay = 500 // ms

  // A direct hit on the selection page must be attributed to a brand-new
  // transaction_id, so nothing may fire until that click has resolved.
  ensureDirectHitTransactionRefresh()
  if (isHoldingConversions) {
    logger.debug(
      `⏸️ Holding Everflow conversion (adv_event_id=${conversionData.adv_event_id}) until the direct-hit transaction_id is issued`
    )
    heldConversions.push(conversionData)
    return
  }

  if (
    isAlternateOidRestrictedSession() &&
    conversionData.adv_event_id !== ALTERNATE_OID_ALLOWED_ADV_EVENT_ID
  ) {
    logger.debug(
      `ℹ️ oid=${getAlternateOidValue()} session: skipping Everflow event adv_event_id=${conversionData.adv_event_id} (only ${ALTERNATE_OID_ALLOWED_ADV_EVENT_ID} allowed)`
    )
    return
  }

  if (typeof window.EF === 'undefined' || !window.EF.conversion) {
    if (retryCount < maxRetries) {
      logger.debug(`⏳ Everflow not ready yet. Retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`)
      setTimeout(() => {
        trackConversion(conversionData, retryCount + 1)
      }, retryDelay)
      return
    }
    logger.warn('⚠️ Everflow not initialized after retries. Conversion not tracked:', conversionData)
    return
  }

  // Hold events while a ZA session is (re-)clicking as Response BPO so they
  // attach to the 33 click rather than an earlier Organic one.
  const holdOfferId = String(conversionData.offer_id ?? getEverflowLandingConfig().organicOfferId)
  if (
    retryCount < maxRetries &&
    (pendingResponseBpoReclicks.has(holdOfferId) || needsResponseBpoReclick(holdOfferId))
  ) {
    logger.debug(`⏳ Waiting for Response BPO click on offer_id=${holdOfferId} before tracking conversion (attempt ${retryCount + 1}/${maxRetries})`)
    setTimeout(() => {
      trackConversion(conversionData, retryCount + 1)
    }, retryDelay)
    return
  }

  try {
    const { conversionOfferId } = getEverflowLandingConfig()
    // If the caller has already set an explicit `offer_id` on the payload
    // (e.g. cross-funnel conversions like the CV-1 NAD+ upsell page that
    // need to attribute to offer 3 even though the URL path resolves to
    // offer 1), respect it as-is. Only fall back to the path-based
    // `conversionOfferId` when the caller hasn't specified one.
    let payload =
      conversionData.offer_id != null
        ? conversionData
        : conversionOfferId != null
          ? { ...conversionData, offer_id: conversionOfferId }
          : conversionData

    // oid=23 / oid=54 use the advertiser default event — omit adv_event_id from payload.
    // Also send case_id (order_id) as email so the advertiser can match the conversion.
    if (isAlternateOidRestrictedSession()) {
      const { adv_event_id: _omitted, ...payloadWithoutAdvEventId } = payload
      payload = {
        ...payloadWithoutAdvEventId,
        email: conversionData.order_id || payloadWithoutAdvEventId.email,
      }
    }

    // #723: ORGANICZA on offer-1 First Purchase (coupon attribution priority
    // needs aid, which callers already set to advertiser 1).
    if (shouldAttachOrganicZaCoupon(payload)) {
      payload = {
        ...payload,
        coupon_code: ORGANIC_ZA_COUPON
      }
      logger.debug('🎟️ Attaching Everflow coupon_code ORGANICZA to purchase conversion')
    }

    window.EF.conversion(payload)
    logger.debug('📊 Everflow Conversion tracked:', payload)
  } catch (error) {
    logger.error('❌ Error tracking Everflow conversion:', error)
  }
}

/**
 * sessionStorage key used to persist Department Media's `aff_id2` after the
 * first time it's seen in the URL. The URL only has `aff_id2` on the landing
 * page; subsequent funnel pages (e.g. /checkout, /checkout/selection) don't.
 * Persisting it lets later DM conversion events still know the user came in
 * via Department Media.
 */
const DM_AFF_ID2_KEY = 'everflow_dm_aff_id2'

/**
 * Get the Department Media `aff_id2` for the current session.
 *
 * Priority:
 *  1. URL `aff_id2` (landing page) — if found, persist into sessionStore so
 *     subsequent pages can still detect a DM session.
 *  2. sessionStore (set on a prior page during this session).
 *
 * @returns {string|null} The aff_id2 value, or null if this is not a DM session.
 */
const getOrPersistAffId2 = () => {
  const fromUrl = getUrlParameter('aff_id2')
  if (fromUrl) {
    if (sessionStore.getItem(DM_AFF_ID2_KEY) !== fromUrl) {
      sessionStore.setItem(DM_AFF_ID2_KEY, fromUrl)
      logger.debug(`💾 Persisted Department Media aff_id2 to session: ${fromUrl}`)
    }
    return fromUrl
  }
  return sessionStore.getItem(DM_AFF_ID2_KEY) || null
}

/**
 * Internal helper: fire a Department Media (`aid: 20`) conversion event.
 *
 * Used by Department Media-specific tracking helpers (Start Quiz, Mid Quiz,
 * Purchase, etc.) when users land via Department Media EF-to-EF direct linking
 * (`aff_id2` in URL).
 *
 * Behavior:
 *  - No-ops when `aff_id2` is not present in the URL
 *  - De-dupes per session via `dedupeKey` so it only fires once per session
 *  - Retries until the EF SDK is ready, then calls `EF.conversion` directly so
 *    that no `offer_id` (used by our default funnel routing) is auto-injected
 *
 * @param {Object} options
 * @param {number} [options.adv_event_id] - Department Media advertiser event ID.
 *   Optional: omit to use the advertiser's default revenue/purchase event.
 * @param {string} options.dedupeKey - sessionStorage key used for per-session dedupe
 * @param {string} options.label - Human-readable label for logging
 * @param {Object} [options.extra] - Additional payload fields to merge into the
 *   conversion call (e.g. `{ amount, order_id }` for Purchase events).
 * @param {number} retryCount - Internal retry counter
 */
const fireDepartmentMediaConversion = ({ adv_event_id, dedupeKey, label, extra = {} }, retryCount = 0) => {
  const maxRetries = 15
  const retryDelay = 700

  if (retryCount === 0) {
    logger.debug(`📥 Department Media ${label} conversion requested`)
  }

  if (isAlternateOidRestrictedSession()) {
    logger.debug(`ℹ️ oid=${getAlternateOidValue()} session: skipping Department Media ${label} conversion`)
    return
  }

  // Same hold as trackConversion: nothing fires on a direct hit until the new
  // transaction_id exists. Waiting on the promise rather than the retry loop
  // keeps the SDK-readiness retry budget intact.
  const directHitRefresh = ensureDirectHitTransactionRefresh()
  if (directHitRefresh && isHoldingConversions) {
    logger.debug(`⏸️ Holding Department Media ${label} conversion until the direct-hit transaction_id is issued`)
    directHitRefresh.then(() =>
      fireDepartmentMediaConversion({ adv_event_id, dedupeKey, label, extra }, retryCount)
    )
    return
  }

  // Only fire when this is a Department Media session.
  // `aff_id2` is in the URL only on the landing page; persisted in sessionStore
  // on first sight so subsequent pages still recognize the DM session.
  if (!getOrPersistAffId2()) {
    logger.debug(`ℹ️ Department Media ${label} skipped — no \`aff_id2\` in URL or session`)
    return
  }

  // Session-level dedupe so we don't double-fire on remounts/navigation
  if (sessionStore.getItem(dedupeKey) === 'true') {
    logger.debug(`ℹ️ Department Media ${label} already tracked in this session, skipping`)
    return
  }

  if (typeof window.EF === 'undefined' || !window.EF.conversion) {
    if (retryCount < maxRetries) {
      logger.debug(`⏳ EF not ready for Department Media ${label} conversion. Retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`)
      setTimeout(
        () => fireDepartmentMediaConversion({ adv_event_id, dedupeKey, label, extra }, retryCount + 1),
        retryDelay
      )
      return
    }
    logger.warn(`⚠️ EF not initialized after retries. Department Media ${label} conversion not tracked.`)
    return
  }

  try {
    const payload = {
      aid: 20,
      ...(adv_event_id != null ? { adv_event_id } : {}),
      ...extra
    }
    window.EF.conversion(payload)
    logger.debug(`📊 Department Media ${label} conversion tracked:`, payload)

    sessionStore.setItem(dedupeKey, 'true')
  } catch (error) {
    logger.error(`❌ Error tracking Department Media ${label} conversion:`, error)
  }
}

/**
 * Track a Department Media (aff_id2) Start Quiz conversion.
 *
 * Fires `EF.conversion({ aid: 20, adv_event_id: 52 })` into the Department Media
 * advertiser account when users land via `aff_id2`. This is in addition to the
 * standard Start Quiz event we send to the default `hu0po4er` account via
 * `trackStartQuizConversion()`.
 */
export const trackDepartmentMediaStartQuizConversion = () => {
  logger.debug('🔍 [Everflow] Tracking Department Media Start Quiz conversion')
  fireDepartmentMediaConversion({
    adv_event_id: 52,
    dedupeKey: 'everflow_dm_start_quiz_tracked',
    label: 'Start Quiz'
  })
}

/**
 * Track a Department Media (aff_id2) Mid Quiz conversion.
 *
 * Fires `EF.conversion({ aid: 20, adv_event_id: 53 })` into the Department Media
 * advertiser account when users land via `aff_id2` and reach the mid-quiz step:
 *  - CV-1 funnel: `how_you_sleep` step (HowYouSleep.jsx)
 *  - NAD funnel: `nad_currently_smoke` step (NadCurrentlySmoke.jsx)
 */
export const trackDepartmentMediaMidQuizConversion = () => {
  fireDepartmentMediaConversion({
    adv_event_id: 53,
    dedupeKey: 'everflow_dm_mid_quiz_tracked',
    label: 'Mid Quiz'
  })
}

/**
 * Track a Department Media (aff_id2) Quiz Completed conversion.
 *
 * Fires `EF.conversion({ aid: 20, adv_event_id: 54 })` into the Department Media
 * advertiser account when users land via `aff_id2` and reach the Checkout page
 * (i.e. they completed the quiz and were approved). Wired into
 * `trackCheckoutPageConversion()` in `everflowConversions.js`.
 */
export const trackDepartmentMediaQuizCompletedConversion = () => {
  fireDepartmentMediaConversion({
    adv_event_id: 54,
    dedupeKey: 'everflow_dm_quiz_completed_tracked',
    label: 'Quiz Completed'
  })
}

/**
 * Track a Department Media (aff_id2) Add to Cart conversion.
 *
 * Fires `EF.conversion({ aid: 20, adv_event_id: 55 })` into the Department Media
 * advertiser account when users land via `aff_id2` and advance from the product/
 * plan selection step to the payment page. CV-1 funnel only — NAD funnel is
 * intentionally excluded (do not wire this into NAD components).
 */
export const trackDepartmentMediaAddToCartConversion = () => {
  fireDepartmentMediaConversion({
    adv_event_id: 55,
    dedupeKey: 'everflow_dm_add_to_cart_tracked',
    label: 'Add to Cart'
  })
}

/**
 * Track a Department Media (aff_id2) Add Payment Info conversion.
 *
 * Fires `EF.conversion({ aid: 20, adv_event_id: 56 })` into the Department Media
 * advertiser account when users land via `aff_id2` and submit valid payment
 * details on the checkout form. Wired into `trackAddPaymentInfoConversion()` in
 * `everflowConversions.js`.
 */
export const trackDepartmentMediaAddPaymentInfoConversion = () => {
  fireDepartmentMediaConversion({
    adv_event_id: 56,
    dedupeKey: 'everflow_dm_add_payment_info_tracked',
    label: 'Add Payment Info'
  })
}

/**
 * Track a Department Media (aff_id2) Purchase conversion.
 *
 * Fires `EF.conversion({ aid: 20, amount, order_id })` into the Department Media
 * advertiser account on successful payment. Note: no `adv_event_id` is sent —
 * this uses Department Media's default revenue/purchase event. Wired into
 * `trackPaymentSuccessfulConversion()`, `trackPaymentSuccessfulAsyncConversion()`,
 * and `trackCvPaymentSuccessfulConversion()` in `everflowConversions.js`.
 *
 * @param {Object} [params]
 * @param {number} [params.amount] - Purchase amount in USD
 * @param {string} [params.orderId] - Order / case identifier
 */
export const trackDepartmentMediaPurchaseConversion = ({ amount, orderId } = {}) => {
  const extra = {}
  if (amount != null && amount !== '') {
    extra.amount = amount
  }
  if (orderId) {
    extra.order_id = orderId
  }

  fireDepartmentMediaConversion({
    dedupeKey: 'everflow_dm_purchase_tracked',
    label: 'Purchase',
    extra
  })
}

/**
 * Get the Everflow transaction ID from URL or generate one
 * @returns {string|null} Transaction ID
 */
export const getEverflowTransactionId = () => {
  if (typeof window.EF !== 'undefined' && window.EF.urlParameter) {
    return window.EF.urlParameter('_ef_transaction_id')
  }
  return getUrlParameter('_ef_transaction_id')
}

/**
 * Read every per-funnel `site_ef_transaction_id_offer_<id>` cookie and
 * return a map keyed by `ef_tid_offer_<id>` so the values can be spread
 * directly into log payloads.
 *
 * Each funnel writes its own `site_ef_transaction_id_offer_<id>` cookie
 * (see `persistSiteTransactionIdCookie`). When a user has visited both
 * /cv-1 (offer_id 1) and /nad (offer_id 3) in the same browser, this
 * returns both transaction IDs side-by-side, e.g.
 *   { ef_tid_offer_1: 'abc...', ef_tid_offer_3: 'def...' }
 *
 * Use this in funnel logging to record each funnel's distinct
 * transaction_id in the same log entry — `funnelData.ef_tid` only carries
 * the *current* funnel's value, so cross-funnel attribution would
 * otherwise be lost.
 *
 * @returns {Object<string, string>} Map of `ef_tid_offer_<id>` →
 *   transaction_id. Returns an empty object when no per-offer cookies
 *   exist (e.g. SSR, fresh session, or before EF.click() resolves).
 */
export const getEverflowTransactionIdsByOffer = () => {
  if (typeof document === 'undefined' || !document.cookie) return {}

  const result = {}
  const prefix = 'site_ef_transaction_id_offer_'
  const rows = document.cookie.split('; ').filter(Boolean)

  for (const row of rows) {
    if (!row.startsWith(prefix)) continue

    const separatorIndex = row.indexOf('=')
    if (separatorIndex === -1) continue

    const cookieName = row.slice(0, separatorIndex)
    const offerId = cookieName.slice(prefix.length)
    if (!offerId) continue

    let value = row.slice(separatorIndex + 1)
    try {
      value = decodeURIComponent(value)
    } catch (_e) {
      // Fall back to raw value if decoding fails.
    }

    // Skip the boolean tracking-marker value used by sibling cookies.
    if (!value || value === 'true') continue

    result[`ef_tid_offer_${offerId}`] = value
  }

  return result
}

/**
 * Get Everflow transaction ID from cookies
 * Everflow stores transaction ID in multiple cookie formats
 * @returns {string|null} Transaction ID or null
 */
export const getEverflowTransactionIdFromCookie = () => {
  // 1) Funnel-specific site cookie (e.g. site_ef_transaction_id_offer_1 for
  //    /cv-1, site_ef_transaction_id_offer_3 for /nad). This is the primary
  //    source so that switching funnels in the same browser yields a
  //    distinct transaction_id per funnel.
  const siteCookieName = getSiteTransactionIdCookieName()
  const siteTransactionId = getEverflowTrackingCookie(siteCookieName) || ''
  logger.debug(`🔍 [Everflow] ${siteCookieName} cookie value:`, siteTransactionId)
  if (siteTransactionId) {
    return siteTransactionId
  }

  // 2) Legacy single-cookie name (kept for back-compat with sessions that
  //    were created before per-funnel cookies existed).
  const legacyTransactionId = getEverflowTrackingCookie('site_ef_transaction_id') || ''
  if (legacyTransactionId) {
    logger.debug('🔍 [Everflow] Falling back to legacy site_ef_transaction_id cookie:', legacyTransactionId)
    return legacyTransactionId
  }

  // 3) EF SDK's own cookie for the current funnel
  //    (ef_tid_c_o_<currentOfferId> preferred, otherwise any ef_tid_c_*).
  const sdkTransactionId = getEfSdkTransactionIdFromCookie()
  if (sdkTransactionId) {
    logger.debug('🔑 Found Everflow transaction ID in SDK cookie:', sdkTransactionId)
    return sdkTransactionId
  }

  // 4) Fallback cookie names
  const cookieNames = ['transaction_id', '_ef_transaction_id']
  logger.debug('🔍 [Everflow] No primary transaction cookie found. Checking fallback cookies:', cookieNames)

  for (const cookieName of cookieNames) {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${cookieName}=`))

    if (cookie) {
      const value = cookie.split('=')[1]
      logger.debug(`🔑 Found Everflow transaction ID in cookie '${cookieName}':`, value)
      return value
    }
  }

  logger.debug('⚠️ [Everflow] No transaction ID found in any cookie')
  return null
}

/**
 * Get all Everflow parameters from cookie
 * Extracts transaction_id and sub parameters (sub1-sub5) from Everflow cookie
 * 
 * IMPORTANT: This function reads from COOKIES first, with URL fallback for missing sub params.
 * The Everflow script (loaded on page) generates the transaction_id and stores it in cookies.
 * 
 * Flow:
 * 1. User clicks affiliate link (e.g., CompareTreatments)
 * 2. Lands on sunlight.com with URL params: ?oid=1&affid=2&sub3=344e188f...
 * 3. Everflow script loads and creates cookies:
 *    - ef_tid_c_a_1 = transaction_id|sub1|sub2|sub3 (affiliate cookie)
 *    - ef_tid_c_o_1 = transaction_id|sub1|sub2|sub3 (offer cookie)
 *    - ef_affid = affiliate_id
 * 4. This function extracts data from those cookies
 * 5. If sub params are missing in cookie, falls back to URL params (for localhost/testing)
 * 
 * Cookie format: transaction_id|sub1|sub2|sub3|sub4|sub5
 * Example: 46a3bb35e83b446b9346596969ce5d6a|d05c2c34e2904f2e804cb6660515fcd1|f9090923bc904d138d3dd72d36a45a05
 *
 * @param {string|number|null} [explicitOfferId] - When provided, the
 *   function runs in STRICT mode and only reads cookies that match this
 *   offer_id (`ef_tid_c_o_<offerId>` and `site_ef_transaction_id_offer_<offerId>`).
 *   It will NOT fall back to other offers' cookies. Use this when
 *   initializing per-funnel state (e.g. /nad's funnelData) so the wrong
 *   funnel's transaction_id can't leak in before EF.click() has fired
 *   for the requested offer. When omitted, behavior is unchanged: prefer
 *   the offer-specific cookie matching the URL, then fall back to any
 *   `ef_tid_c_a_*` / `ef_tid_c_o_*` cookie.
 * @returns {Object|null} Object with transaction_id and sub parameters, or null if not found
 */
export const getEverflowParams = (explicitOfferId = null) => {
  logger.debug('🔍 [Everflow] Attempting to extract params from cookie (NOT URL)...')

  // STRICT vs LAX cookie selection
  //
  // - LAX (no `explicitOfferId`): callers want "whatever EF wrote on this
  //   page". We prefer the offer-specific cookie matching the URL but fall
  //   back to any `ef_tid_c_a_*` / `ef_tid_c_o_*` cookie if needed.
  // - STRICT (`explicitOfferId` provided): callers want params for a
  //   specific funnel (e.g. when initializing /nad's funnelData). We MUST
  //   only read `ef_tid_c_o_<explicitOfferId>` and the matching site
  //   cookie — falling back to any other offer's cookie would silently
  //   contaminate the funnel's stored ef_tid with a sibling funnel's
  //   transaction_id (e.g. /nad picking up /cv-1's value before EF.click()
  //   has fired for offer_id 3).
  const isStrictOfferLookup = explicitOfferId !== null && explicitOfferId !== undefined
  const offerIdForLookup = isStrictOfferLookup
    ? String(explicitOfferId)
    : String(getEverflowLandingConfig().organicOfferId)

  if (isStrictOfferLookup) {
    logger.debug(`🔒 [Everflow] Strict offer lookup for offer_id=${offerIdForLookup}`)
  }

  // Try to find dynamic Everflow cookies (ef_tid_c_a_* or ef_tid_c_o_*)
  // These cookies contain: transaction_id|sub1|sub2|sub3|...
  const cookieRows = document.cookie.split('; ').filter(Boolean)
  const offerSpecificPrefix = `ef_tid_c_o_${offerIdForLookup}=`

  let efCookie = null
  let cookieName = null

  const offerSpecificCookie = cookieRows.find((row) => row.startsWith(offerSpecificPrefix))
  const dynamicCookie =
    offerSpecificCookie ||
    (isStrictOfferLookup
      ? null
      : cookieRows.find(
          (row) => row.startsWith('ef_tid_c_a_') || row.startsWith('ef_tid_c_o_')
        ))

  if (dynamicCookie) {
    const separatorIndex = dynamicCookie.indexOf('=')
    cookieName = dynamicCookie.slice(0, separatorIndex)
    efCookie = dynamicCookie
    logger.debug(`🔍 [Everflow] Found cookie: ${cookieName}`)
  }
  
  if (!efCookie) {
    logger.debug('⚠️ [Everflow] ef_tid_c_a_* or ef_tid_c_o_* cookie not found (Everflow script may not have loaded yet)')

    // Fallback: try to get just transaction_id from the funnel-specific
    // site cookie. In strict mode we ONLY look at the cookie matching
    // `explicitOfferId` and skip the legacy single-cookie fallback to
    // avoid cross-funnel contamination.
    const siteCookieName = isStrictOfferLookup
      ? `site_ef_transaction_id_offer_${offerIdForLookup}`
      : getSiteTransactionIdCookieName()
    const transactionId =
      getEverflowTrackingCookie(siteCookieName) ||
      (isStrictOfferLookup
        ? ''
        : getEverflowTrackingCookie('site_ef_transaction_id')) ||
      ''
    logger.debug(`🔍 [Everflow] ${siteCookieName} fallback value:`, transactionId)
    if (transactionId) {
      logger.debug('🔑 [Everflow] Found transaction_id in alternate cookie, but no sub params available')
      return {
        transaction_id: transactionId,
        sub1: '',
        sub2: '',
        sub3: '',
        sub4: '',
        sub5: ''
      }
    }
    
    logger.debug('❌ [Everflow] No Everflow cookies found. Script may not have loaded yet.')
    return null
  }
  
  // Extract cookie value (everything after "cookie_name=")
  const value = efCookie.split('=')[1]
  
  // Decode if URL encoded
  const decodedValue = decodeURIComponent(value)
  
  logger.debug(`🔍 [Everflow] Raw cookie value from ${cookieName}:`, decodedValue)
  
  // Split by pipe character
  // Format: transaction_id|sub1|sub2|sub3|sub4|sub5
  const parts = decodedValue.split('|')
  
  let params = {
    transaction_id: parts[0] || '',
    sub1: parts[1] || '',
    sub2: parts[2] || '',
    sub3: parts[3] || '',  // Partner identifier (e.g., CompareTreatments)
    sub4: parts[4] || '',
    sub5: parts[5] || ''
  }
  
  logger.debug('✅ [Everflow] Successfully extracted params from cookie:', params)
  
  // Fallback: If sub parameters are empty in cookie, try to get them from URL
  // This is useful for localhost testing where Everflow script may not populate cookie fully
  const urlParams = new URLSearchParams(window.location.search)
  let foundUrlParams = false
  
  if (!params.sub1 && urlParams.has('sub1')) {
    params.sub1 = urlParams.get('sub1')
    foundUrlParams = true
  }
  if (!params.sub2 && urlParams.has('sub2')) {
    params.sub2 = urlParams.get('sub2')
    foundUrlParams = true
  }
  if (!params.sub3 && urlParams.has('sub3')) {
    params.sub3 = urlParams.get('sub3')
    foundUrlParams = true
    logger.debug('🔄 [Everflow] sub3 not in cookie, using URL fallback:', params.sub3)
  }
  if (!params.sub4 && urlParams.has('sub4')) {
    params.sub4 = urlParams.get('sub4')
    foundUrlParams = true
  }
  if (!params.sub5 && urlParams.has('sub5')) {
    params.sub5 = urlParams.get('sub5')
    foundUrlParams = true
  }
  
  if (foundUrlParams) {
    logger.debug('🔄 [Everflow] Applied URL fallback for missing sub params:', params)
  }
  
  // Warn if transaction_id is empty (shouldn't happen if cookie exists)
  if (!params.transaction_id) {
    logger.warn('⚠️ [Everflow] Cookie exists but transaction_id is empty!')
  }
  
  // Log if sub parameters are present
  if (params.sub1) logger.debug('🎯 [Everflow] sub1 detected:', params.sub1)
  if (params.sub2) logger.debug('🎯 [Everflow] sub2 detected:', params.sub2)
  if (params.sub3) {
    logger.debug('🎯 [Everflow] sub3 detected (Partner ID):', params.sub3)
  } else {
    logger.debug('ℹ️ [Everflow] No sub3 found in cookie or URL - may show as Organic in Everflow')
  }
  if (params.sub4) logger.debug('🎯 [Everflow] sub4 detected:', params.sub4)
  if (params.sub5) logger.debug('🎯 [Everflow] sub5 detected:', params.sub5)
  
  return params
}

/**
 * Check if Everflow is ready for tracking
 * @returns {boolean} True if Everflow is initialized and ready
 */
export const isEverflowReady = () => {
  return typeof window.EF !== 'undefined' && 
         typeof window.EF.conversion === 'function'
}

/**
 * Wait for Everflow to be ready
 * @param {number} timeout - Maximum wait time in ms (default 5000)
 * @returns {Promise<boolean>} Resolves to true if ready, false if timeout
 */
export const waitForEverflow = (timeout = 5000) => {
  return new Promise((resolve) => {
    if (isEverflowReady()) {
      resolve(true)
      return
    }

    const startTime = Date.now()
    const checkInterval = setInterval(() => {
      if (isEverflowReady()) {
        clearInterval(checkInterval)
        logger.debug('✅ Everflow ready for tracking')
        resolve(true)
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval)
        logger.warn('⚠️ Everflow initialization timeout')
        resolve(false)
      }
    }, 100)
  })
}

/**
 * Clear Everflow session tracking (for testing purposes)
 */
export const clearEverflowSession = () => {
  clearEverflowTrackingCookie()
  // Also clear ORGANICZA tag so tests can re-run geo tagging cleanly
  if (typeof document !== 'undefined') {
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    const domainPart = isLocalhost ? '' : '; domain=.sunlight.com'
    document.cookie = `everflow_coupon_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}; SameSite=Lax`
  }
  sessionStore.removeItem('everflow_coupon_code')
  logger.debug('🗑️ Everflow session tracking cleared')
}

export default {
  initializeEverflow,
  createEverflowTransactionForOffer,
  trackConversion,
  trackDepartmentMediaStartQuizConversion,
  trackDepartmentMediaMidQuizConversion,
  trackDepartmentMediaQuizCompletedConversion,
  trackDepartmentMediaAddToCartConversion,
  trackDepartmentMediaAddPaymentInfoConversion,
  trackDepartmentMediaPurchaseConversion,
  getEverflowTransactionId,
  getEverflowTransactionIdFromCookie,
  getEverflowTransactionIdsByOffer,
  getEverflowParams,
  getOrganicZaCouponCode,
  maybeTagOrganicZaCoupon,
  getVisitorCountry,
  isPartnerAffiliateTraffic,
  ORGANIC_ZA_COUPON,
  isEverflowReady,
  waitForEverflow,
  clearEverflowSession
}
